import {
  HeadlessUniversalCheckoutOptions,
  PaymentFlow,
  PaymentHandling,
  PaymentInstrumentType,
} from '@primer-io/checkout-web'

import { PageId } from 'page-constants'
import { Cohort, Color, Language } from 'root-constants'

export const enum SubscriptionPeriod {
  MONTHLY = 1,
  QUARTERLY = 3,
  SEMI_ANNUALLY = 6,
  YEARLY = 12,
}

export const enum AnimationState {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export const FINAL_POPUP_TAP = {
  SUCCESS: 'proceed_to_my_plan',
  ERROR: 'try_again',
}

export const EMPTY_FIELD_ERROR = `can't be blank`

export const DEFAULT_CARDHOLDER_NAME = ' '

export const DEFAULT_ERROR_DESCRIPTION =
  'Please check your payment information or contact your bank and try again, or try a new payment method.'

export const enum StripeErrorCode {
  CARD_DECLINED = 'card_declined',
}

export const enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  PAYPAL = 'pay_pal',
  UPI = 'upi',
}

export const enum PaymentSystem {
  PAYPAL = 'paypal',
  PRIMER = 'primer',
  STRIPE = 'stripe',
}

export const PrimerPaymentInstrumentMap = {
  [PaymentInstrumentType.CARD]: PaymentMethod.CREDIT_CARD,
  [PaymentInstrumentType.PAYPAL]: PaymentMethod.PAYPAL,
  [PaymentInstrumentType.PAYPAL_VAULTED]: PaymentMethod.PAYPAL,
} as const

export const enum SourceStep {
  PAYWALL = 'paywall',
  CHECKOUT = 'checkout',
}

export type TPaymentHandling = 'AUTO' | 'MANUAL'

export type TCardPaymentSystem = PaymentSystem.PRIMER | PaymentSystem.STRIPE

export const THREEDS_REDIRECT_SEARCH_PARAM = 'threeds'

export const enum StripeSoftDeclineReason {
  AUTHENTICATION_FAILURE = 'payment_intent_authentication_failure',
  DO_NOT_HONOR = 'do_not_honor',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  INVALID_AMOUNT = 'invalid_amount',
  PROCESSING_ERROR = 'processing_error',
  REENTER_TRANSACTION = 'reenter_transaction',
  SERVICE_NOT_ALLOWED = 'service_not_allowed',
  TRANSACTION_NOT_ALLOWED = 'transaction_not_allowed',
  CARD_VELOCITY_EXCEEDED = 'card_velocity_exceeded',
  GENERIC_DECLINE = 'generic_decline',
  TRY_AGAIN_LATER = 'try_again_later',
}

export const enum StripeDeclineReason {
  STOLEN_CARD = 'stolen_card',
  LOST_CARD = 'lost_card',
  STRIPE_GENERIC_DECLINE_ERROR = 'generic_decline',
}

export const STRIPE_SOFT_DECLINE_REASONS = [
  StripeSoftDeclineReason.AUTHENTICATION_FAILURE,
  StripeSoftDeclineReason.DO_NOT_HONOR,
  StripeSoftDeclineReason.INSUFFICIENT_FUNDS,
  StripeSoftDeclineReason.INVALID_AMOUNT,
  StripeSoftDeclineReason.PROCESSING_ERROR,
  StripeSoftDeclineReason.REENTER_TRANSACTION,
  StripeSoftDeclineReason.SERVICE_NOT_ALLOWED,
  StripeSoftDeclineReason.TRANSACTION_NOT_ALLOWED,
  StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED,
  StripeSoftDeclineReason.GENERIC_DECLINE,
  StripeSoftDeclineReason.TRY_AGAIN_LATER,
]

export const enum Currency {
  USD = 'usd',
  EUR = 'eur',
  GBP = 'gbp',
  NZD = 'nzd',
  MXN = 'mxn',
  SAR = 'sar',
  AED = 'aed',
  EGP = 'egp',
  TRY = 'try',
  SGD = 'sgd',
  NGN = 'ngn',
  CAD = 'cad',
  ILS = 'ils',
  MYR = 'myr',
  IDR = 'idr',
  CHF = 'chf',
  QAR = 'qar',
  ZAR = 'zar',
  MAD = 'mad',
  AUD = 'aud',
  BDT = 'bdt',
  INR = 'inr',
}

export const CURRENCY_SYMBOLS = {
  [Currency.USD]: '$',
  [Currency.EUR]: '€',
  [Currency.GBP]: '£',
  [Currency.NZD]: '\u2066NZ$\u2069',
  [Currency.MXN]: 'MXN',
  [Currency.SAR]: 'SR',
  [Currency.AED]: 'Dh',
  [Currency.EGP]: '\u2066E£\u2069',
  [Currency.TRY]: '₺',
  [Currency.SGD]: '\u2066S$\u2069',
  [Currency.NGN]: '₦',
  [Currency.CAD]: '\u2066CA$\u2069',
  [Currency.ILS]: '₪',
  [Currency.MYR]: 'RM',
  [Currency.IDR]: 'Rp',
  [Currency.QAR]: 'QR',
  [Currency.ZAR]: 'R',
  [Currency.MAD]: 'DH',
  [Currency.AUD]: '\u2066A$\u2069',
  [Currency.BDT]: '৳',
  [Currency.INR]: '₹',
}

export const INITIAL_PRIMER_CONFIG = {
  paymentHandling: 'MANUAL' as PaymentHandling,
  paypal: {
    paymentFlow: PaymentFlow.PREFER_VAULT,
  },
  card: {
    cardholderName: {
      required: false,
    },
  },
} satisfies Partial<HeadlessUniversalCheckoutOptions>

export const PRIMER_PAY_PAL_ID = 'paypal-button'

export const PRIMER_PAYMENT_ERRORS = {
  INSUFFICIENT_FUNDS: 'purchase1.insufficientFunds',
  PAYMENT_FAILED: 'purchase1.insufficientFunds',
  DECLINED: 'purchase1.declinedError',
  VELOCITY_EXCEEDED: 'purchase1.velocityExceeded',
  AUTHENTICATION_REQUIRED: 'purchase1.authenticationRequired',
  WITHDRAWAL_LIMIT_EXCEEDED: 'purchase1.withdrawLimit',
  EXPIRED_CARD: 'purchase1.expiredCard',
  COMMON_ERROR: 'commonComponents.commonError',
  ERROR: 'commonComponents.commonError',
  INVALID_CARD_NUMBER: 'commonComponents.commonError',
  LOST_OR_STOLEN_CARD: 'commonComponents.commonError',
  SUSPECTED_FRAUD: 'commonComponents.commonError',
  UNKNOWN: 'commonComponents.commonError',
  REFER_TO_CARD_ISSUER: 'commonComponents.commonError',
  DO_NOT_HONOR: 'commonComponents.commonError',
  ISSUER_TEMPORARILY_UNAVAILABLE: 'commonComponents.commonError',
  CONNECTION_ERROR: 'commonComponents.connectionError',
}

export const CARD_PAYMENT_FIELD_NAMES = {
  NAME: 'name',
  NUMBER: 'cardNumber',
  EXPIRY: 'cardExpiry',
  CVC: 'cardCvc',
} as const

export const PRIMER_ERROR_CODE = {
  CARD_REQUIRED: 'cardRequired',
  CARD_IS_INCOMPLETE: 'cardIncomplete',
  CARD_IS_INVALID: 'cardInvalid',
  EXPIRY_REQUIRED: 'expiryRequired',
  CARD_IS_EXPIRED: 'cardExpired',
  CARD_EXPIRY_YEAR_INVALID: 'expiryYearInvalid',
  CARD_EXPIRY_MONTH_INVALID: 'expiryMonthInvalid',
  CVV_IS_INCOMPLETE: 'cvvIncomplete',
  CVV_IS_INVALID: 'cvvInvalid3',
}

export const PRIMER_CARD_FIELD_ID = {
  number: 'primer-card-number-input',
  expiry: 'primer-card-expiry-input',
  cvc: 'primer-card-cvv-input',
  holder: 'primer-card-holder-input',
}

export const CARDHOLDER_NAME_REGEX =
  /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/

export const UPI_ID_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z]{3,}$/

export const INDIA_POSTAL_CODE_REGEX = /^\d{6}$/

export const INDIA_POSTAL_CODE_LENGTH = 6

export const UPI_FIELD_MAX_LENGTH = 255

export const UPI_BILLING_COUNTRY = 'IN'

export const TextPosition = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const

export const LOWER_PRICES_TESTORA_TEST_COHORTS: Cohort[] = [
  Cohort.AP_QURAN_313_CT,
  Cohort.KP_BS_13_CT,
]

export const AYAH_SOCIAL_PROOF_COHORTS = [
  Cohort.FLOW_AYAH_314,
  Cohort.FLOW_AYAH_314_C,
]

export const COHORTS_WITH_PRICE_UPDATE_ON_TIMER_END: readonly Cohort[] = [
  Cohort.AP_PA_440_AM,
  Cohort.AP_FINANCE_313_AM,
  Cohort.FLOW_AYAH_313_AM,
  Cohort.AP_PA_440_C_AM,
  Cohort.AP_FINANCE_313_C_AM,
  Cohort.AP_QURAN_313_C_AM,
  Cohort.FINAL_MONTHLY_AYAH_AM,
  Cohort.FINAL_MONTHLY_AM,
  Cohort.FLOW_1_INTRO_LONG_2271_AM,
  Cohort.FINAL_MONTHLY_KARMA_AM,
  Cohort.KP_BS_13_AM,
]

export const AUTO_BUNDLE_UPSELL_PAGES: PageId[] = [
  PageId.UPSELL_GUIDE,
  PageId.UPSELL_GUIDE_V2,
  PageId.UPSELL_AYAH_PA,
  PageId.UPSELL_PARENTING,
]

export const AUTO_BUNDLE_UPSELL_LOCALES_BY_COHORT: Partial<
  Record<Cohort, Language[]>
> = {
  // KarmaPath cohorts
  [Cohort.KP_BS_13_C]: [Language.EN],
  [Cohort.KP_BS_13]: [Language.EN],
  [Cohort.KP_BS_7_WTYPE]: [Language.EN],
  [Cohort.KP_BS_7_WTYPE_C]: [Language.EN],
  // AyahPath cohorts
  [Cohort.AP_QURAN_313_C]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.ID,
    Language.TR,
  ],
  [Cohort.AP_KNOWLEDGE_428_C]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.ID,
    Language.TR,
  ],
  [Cohort.AP_QURAN_313_CF]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.ID,
    Language.TR,
  ],
  [Cohort.AP_QURAN_313_C_AM]: [Language.EN],
  [Cohort.AP_QURAN_313_CC]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
  ],
  [Cohort.AP_PA_440_C]: [Language.EN],
  [Cohort.AP_PA_440]: [Language.EN],
  [Cohort.AP_PA_440_AM]: [Language.EN],
  [Cohort.AP_PA_440_C_AM]: [Language.EN],
  [Cohort.FLOW_AYAH_314]: [Language.EN],
  [Cohort.FLOW_AYAH_314_C]: [Language.EN],
  [Cohort.AP_FINANCE_313_C]: [Language.EN, Language.AR, Language.FR],
  [Cohort.AP_FINANCE_313]: [Language.EN, Language.AR, Language.FR],
  [Cohort.AP_FINANCE_313_C_AM]: [Language.EN],
  [Cohort.AP_FINANCE_313_AM]: [Language.EN],
  [Cohort.AP_SD_313_C]: [Language.EN],
  [Cohort.AP_SD_313_CC]: [Language.EN],
  [Cohort.AP_PARENTING_313_C]: [Language.EN],
  [Cohort.FLOW_AYAH_313]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.ID,
    Language.TR,
  ],
}

export const YEARLY_TRIAL_PERIOD_DAYS = [364, 365]

export const DISABLED_PAYMENT_BUTTON_BACKGROUND = `var(--disabled-button-background, ${Color.SKY_LIGHT})`
